import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react'
import { cn } from '@/lib/cn'
import { Spinner } from './Spinner'

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'accent'
/** `touch` (48px) and `xl` (56px) are for phone screens — gloved / dusty hands at the gate. */
export type ButtonSize = 'sm' | 'md' | 'touch' | 'xl'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
  fullWidth?: boolean
  ref?: Ref<HTMLButtonElement>
}

const base =
  'inline-flex select-none items-center justify-center gap-2 rounded-chip border font-mono font-bold uppercase tracking-label leading-none transition-[background-color,color,border-color,opacity] duration-150 disabled:cursor-not-allowed disabled:opacity-45 focus-visible:outline-none [&>svg]:h-[1.3em] [&>svg]:w-[1.3em] [&>svg]:shrink-0'

const variants: Record<ButtonVariant, string> = {
  primary: 'border-ink bg-ink text-paper hover:bg-ink/85 active:bg-ink/75',
  secondary: 'border-ink/80 bg-transparent text-ink hover:bg-ink hover:text-paper active:bg-ink/85',
  accent: 'border-amber bg-amber text-white hover:border-amber-dark hover:bg-amber-dark',
  danger: 'border-danger bg-danger text-white hover:bg-danger/90 active:bg-danger/80',
  ghost: 'border-transparent bg-transparent text-ink hover:bg-ink/5 active:bg-ink/10',
}

const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[11px]',
  md: 'h-10 px-4 text-[12px]',
  touch: 'min-h-touch px-5 text-[13px]',
  xl: 'min-h-touch-lg px-6 text-[14px]',
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  leadingIcon,
  trailingIcon,
  fullWidth,
  className,
  children,
  disabled,
  type = 'button',
  ref,
  ...rest
}: ButtonProps) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        // ui-btn* hooks let a surface restyle buttons (the phone theme, index.css).
        'ui-btn',
        `ui-btn-${variant}`,
        `ui-btn-${size}`,
        base,
        variants[variant],
        sizes[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : leadingIcon}
      {children}
      {!loading && trailingIcon}
    </button>
  )
}

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Accessible name — required because the button has no text. */
  label: string
  icon: ReactNode
  size?: 'sm' | 'md' | 'touch'
  variant?: 'ghost' | 'secondary'
  ref?: Ref<HTMLButtonElement>
}

export function IconButton({
  label,
  icon,
  size = 'md',
  variant = 'ghost',
  className,
  type = 'button',
  ref,
  ...rest
}: IconButtonProps) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-chip text-ink transition-colors disabled:opacity-40',
        variant === 'ghost'
          ? 'hover:bg-ink/5 active:bg-ink/10'
          : 'border border-rule bg-surface hover:border-ink/40',
        size === 'sm' && 'h-8 w-8 text-[16px]',
        size === 'md' && 'h-10 w-10 text-[20px]',
        size === 'touch' && 'h-12 w-12 text-[22px]',
        className,
      )}
      {...rest}
    >
      {icon}
    </button>
  )
}
